/**
 * COMPTABILITÉ — LE RÉCAP DE LA NUIT.
 *
 * Tout ce que les annexes ZAP racontent, rassemblé et cherchable : les
 * offerts carte serveur ticket par ticket, les doses servies et les
 * changements de bouteilles, les produits vendus, les ouvertures de tiroirs,
 * le détail et les notes de chaque caisse.
 *
 * À LIRE SI L'ON VEUT — l'étape n'est pas obligatoire et rien ici ne change
 * la clôture. On doit pouvoir tout y retrouver vite : un champ de recherche
 * filtre tous les onglets à la fois, et chaque bloc ouvre ses PDF.
 */

import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Gift, GlassWater, Package, Search, Store, Vault } from 'lucide-react'
import { Tabs } from '../admin/primitives.js'
import { formatMoney } from './money.js'
import { EnCours, Indisponible, heureParis, lireJson, type Etat } from './ClosingCommon.js'
import { VoirSource, type DocVisible } from './SourceViewer.js'
import './finance-recap.css'

interface Recap {
  business_date: string
  doses: Array<{ produit: string; doses: number; volume_l: number; par_rampe: Array<{ rampe: string; doses: number }> }>
  doses_total: number
  bouteilles: Array<{ rampe: string; doseur: string; produit: string; at: string | null }>
  bouteilles_incorrectes: Array<{ rampe: string; doseur: string; doses: number | null; at: string | null }>
  offerts: {
    total_cents: number
    titulaires: Array<{
      nom: string; total_cents: number | null; paye_cents: number; remise_cents: number
      consommations: Array<{ at: string | null; caisse: string | null; paye_cents: number; remise_cents: number; articles: Array<{ qty: number; produit: string; cents: number | null }> }>
    }>
  } | null
  tiroirs: Array<{ salle: string | null; caisse: string; ouvertures: string[] }>
  produits: Array<{ produit: string; qty: number }>
  caisses: Array<{ caisse: string; par_moyen: Record<string, number>; total_cents: number | null }>
  commentaires: string[]
  pieces: Array<{ id: string; filename: string; kind: string }>
}

const commeRecap = (v: unknown): Recap | null =>
  typeof v === 'object' && v !== null && Array.isArray((v as Record<string, unknown>)['doses']) ? (v as Recap) : null

/** « Rampe Bar Planete 1 » → « Planete 1 » : la rampe se lit par sa salle. */
const rampeCourte = (r: string): string => r.replace(/^Rampe\s+Bar\s+/i, '')
const caisseCourte = (c: string): string => c.replace(/^Caisse\s+(Bar\s+)?/i, '')

type Onglet = 'offerts' | 'doses' | 'produits' | 'tiroirs' | 'caisses'

export function NightRecap(props: { nuit: string }) {
  const [recap, setRecap] = useState<Etat<Recap>>({ etat: 'chargement' })
  const [onglet, setOnglet] = useState<Onglet>('offerts')
  const [q, setQ] = useState('')

  useEffect(() => {
    void lireJson<Recap>(`/api/v1/finance/nights/${props.nuit}/recap`, commeRecap).then(setRecap)
  }, [props.nuit])

  const r = recap.etat === 'ok' ? recap.valeur : null
  const cherche = q.trim().toLowerCase()
  const trouve = (...t: Array<string | null | undefined>): boolean => cherche === '' || t.some((x) => (x ?? '').toLowerCase().includes(cherche))

  const pieces = (kind: string): DocVisible[] => (r?.pieces ?? []).filter((p) => p.kind === kind)
    .map((p) => ({ id: p.id, filename: p.filename, provider: 'ZAP_SIS', report_type: 'ZAP_OTHER', business_date: props.nuit }))

  const ouvertures = useMemo(() => (r?.tiroirs ?? []).reduce((s, t) => s + t.ouvertures.length, 0), [r])
  const produitsTotal = useMemo(() => (r?.produits ?? []).reduce((s, p) => s + p.qty, 0), [r])
  const maxProduit = Math.max(1, ...(r?.produits ?? []).map((p) => p.qty))

  return (
    <section className="lc-panel">
      <div className="lc-panel__head">
        <span className="fih-head__icon" aria-hidden="true"><Package size={17} /></span>
        <h2 className="lc-panel__title">Récap de la nuit</h2>
        <span className="lc-panel__spacer" />
        <span className="lc-hint">À lire si vous voulez — rien ici ne change la clôture.</span>
      </div>
      <div className="lc-panel__body">
        {recap.etat === 'chargement' ? <EnCours quoi="du récap" /> : null}
        {recap.etat === 'absent' ? <Indisponible titre="Le récap n’a pas pu être lu" message={recap.message} /> : null}
        {r && r.pieces.length === 0 ? (
          <p className="fi-empty">
            Aucune annexe ZAP lue pour cette nuit : les doses, offerts et tiroirs apparaîtront ici dès qu’elles
            seront reçues.
          </p>
        ) : null}

        {r && r.pieces.length > 0 ? (
          <>
            <div className="frc-kpis">
              <button type="button" className={`frc-kpi${onglet === 'offerts' ? ' frc-kpi--on' : ''}`} onClick={() => setOnglet('offerts')}>
                <span className="frc-kpi__label"><Gift size={14} aria-hidden="true" /> Offerts</span>
                <span className="frc-kpi__valeur">{r.offerts ? formatMoney(r.offerts.total_cents) : '—'}</span>
                <span className="frc-kpi__sous">{r.offerts ? `${r.offerts.titulaires.length} carte${r.offerts.titulaires.length > 1 ? 's' : ''} serveur` : 'droit requis'}</span>
              </button>
              <button type="button" className={`frc-kpi${onglet === 'doses' ? ' frc-kpi--on' : ''}`} onClick={() => setOnglet('doses')}>
                <span className="frc-kpi__label"><GlassWater size={14} aria-hidden="true" /> Doses servies</span>
                <span className="frc-kpi__valeur">{r.doses_total}</span>
                <span className="frc-kpi__sous">
                  {r.bouteilles.length} bouteille{r.bouteilles.length > 1 ? 's' : ''} changée{r.bouteilles.length > 1 ? 's' : ''}
                  {r.bouteilles_incorrectes.length > 0 ? ` · ${r.bouteilles_incorrectes.length} signalée${r.bouteilles_incorrectes.length > 1 ? 's' : ''}` : ''}
                </span>
              </button>
              <button type="button" className={`frc-kpi${onglet === 'produits' ? ' frc-kpi--on' : ''}`} onClick={() => setOnglet('produits')}>
                <span className="frc-kpi__label"><Package size={14} aria-hidden="true" /> Produits vendus</span>
                <span className="frc-kpi__valeur">{produitsTotal}</span>
                <span className="frc-kpi__sous">{r.produits.length} références</span>
              </button>
              <button type="button" className={`frc-kpi${onglet === 'tiroirs' ? ' frc-kpi--on' : ''}`} onClick={() => setOnglet('tiroirs')}>
                <span className="frc-kpi__label"><Vault size={14} aria-hidden="true" /> Tiroirs ouverts</span>
                <span className="frc-kpi__valeur">{ouvertures}</span>
                <span className="frc-kpi__sous">{r.tiroirs.length} caisse{r.tiroirs.length > 1 ? 's' : ''}</span>
              </button>
              <button type="button" className={`frc-kpi${onglet === 'caisses' ? ' frc-kpi--on' : ''}`} onClick={() => setOnglet('caisses')}>
                <span className="frc-kpi__label"><Store size={14} aria-hidden="true" /> Caisses</span>
                <span className="frc-kpi__valeur">{r.caisses.length}</span>
                <span className="frc-kpi__sous">{r.commentaires.length} note{r.commentaires.length > 1 ? 's' : ''} de ZAP</span>
              </button>
            </div>

            <div className="frc-barre">
              <Tabs
                tabs={[
                  { key: 'offerts', label: 'Offerts' }, { key: 'doses', label: 'Doses & bouteilles' },
                  { key: 'produits', label: 'Produits' }, { key: 'tiroirs', label: 'Tiroirs' }, { key: 'caisses', label: 'Caisses & notes' },
                ]}
                active={onglet}
                onChange={(k) => setOnglet(k as Onglet)}
              />
              <label className="frc-recherche">
                <Search size={15} aria-hidden="true" />
                <input className="lc-input" placeholder="Produit, caisse ou carte" value={q}
                  onChange={(e) => setQ(e.target.value)} aria-label="Chercher dans le récap" />
              </label>
            </div>

            {onglet === 'offerts' ? (
              r.offerts === null ? (
                <p className="fi-empty">Votre rôle ne permet pas de lire les offerts.</p>
              ) : (
                <div className="frc-cartes">
                  {r.offerts.titulaires.filter((t) => trouve(t.nom, ...t.consommations.flatMap((c) => [c.caisse, ...c.articles.map((a) => a.produit)])))
                    .map((t) => (
                      <article key={t.nom} className="frc-carte">
                        <header className="frc-carte__tete">
                          <span className="frc-carte__nom">{t.nom}</span>
                          <span className="frc-carte__total fi-money">{formatMoney(t.total_cents ?? t.paye_cents + t.remise_cents)}</span>
                        </header>
                        <p className="frc-carte__sous">
                          {formatMoney(t.paye_cents)} offerts · {formatMoney(t.remise_cents)} de remises · {t.consommations.length} passage{t.consommations.length > 1 ? 's' : ''}
                        </p>
                        <ul className="frc-conso">
                          {t.consommations.filter((c) => trouve(t.nom, c.caisse, ...c.articles.map((a) => a.produit))).map((c, i) => (
                            <li key={i}>
                              <span className="frc-conso__heure">{c.at ? heureParis(c.at) : '—'}</span>
                              <span className="frc-conso__quoi">
                                {c.articles.map((a) => `${a.qty} × ${a.produit}`).join(', ')}
                                <span className="frc-conso__ou">{c.caisse ? caisseCourte(c.caisse) : ''}</span>
                              </span>
                              <span className={`frc-conso__montant fi-money${c.remise_cents > 0 && c.paye_cents === 0 ? ' frc-conso__montant--remise' : ''}`}>
                                {formatMoney(c.paye_cents + c.remise_cents)}
                                <span className="frc-conso__type">{c.paye_cents > 0 && c.remise_cents > 0 ? 'offert + remise' : c.remise_cents > 0 ? 'remise' : 'offert'}</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </article>
                    ))}
                </div>
              )
            ) : null}

            {onglet === 'doses' ? (
              <div className="frc-deux">
                <div>
                  <h3 className="frc-titre">Doses servies, toutes rampes</h3>
                  <ul className="frc-barres">
                    {r.doses.filter((d) => trouve(d.produit, ...d.par_rampe.map((p) => p.rampe))).map((d) => (
                      <li key={d.produit}>
                        <span className="frc-barres__nom">{d.produit}</span>
                        <span className="frc-barres__piste"><span className="frc-barres__barre" style={{ width: `${Math.round((d.doses / Math.max(1, r.doses[0]?.doses ?? 1)) * 100)}%` }} /></span>
                        <span className="frc-barres__val">{d.doses}<span className="frc-barres__unite"> · {d.volume_l.toLocaleString('fr-FR')} l</span></span>
                        <span className="frc-barres__detail">{d.par_rampe.map((p) => `${rampeCourte(p.rampe)} ${p.doses}`).join(' · ')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="frc-titre">Changements de bouteilles</h3>
                  {r.bouteilles.length === 0 ? <p className="lc-hint">Aucun changement enregistré.</p> : (
                    <ul className="frc-temps">
                      {r.bouteilles.filter((b) => trouve(b.produit, b.rampe)).map((b, i) => (
                        <li key={i}>
                          <span className="frc-temps__heure">{b.at ? heureParis(b.at) : '—'}</span>
                          <span className="frc-temps__quoi"><strong>{b.produit}</strong> · {rampeCourte(b.rampe)} · {b.doseur.toLowerCase()}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {r.bouteilles_incorrectes.length > 0 ? (
                    <details className="frc-incorrects">
                      <summary><AlertTriangle size={14} aria-hidden="true" /> {r.bouteilles_incorrectes.length} changement{r.bouteilles_incorrectes.length > 1 ? 's' : ''} signalé{r.bouteilles_incorrectes.length > 1 ? 's' : ''} « incorrect{r.bouteilles_incorrectes.length > 1 ? 's' : ''} » par ZAP</summary>
                      <p className="lc-hint">Le nombre indiqué est celui des doses servies entre deux changements de bouteille : un nombre bas peut signaler une bouteille changée trop tôt.</p>
                      <ul className="frc-temps">
                        {r.bouteilles_incorrectes.filter((b) => trouve(b.rampe)).map((b, i) => (
                          <li key={i}>
                            <span className="frc-temps__heure">{b.at ? heureParis(b.at) : '—'}</span>
                            <span className="frc-temps__quoi">{rampeCourte(b.rampe)} · {b.doseur.toLowerCase()} · {b.doses ?? '?'} dose{(b.doses ?? 0) > 1 ? 's' : ''}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </div>
              </div>
            ) : null}

            {onglet === 'produits' ? (
              <ul className="frc-barres frc-barres--produits">
                {r.produits.filter((p) => trouve(p.produit)).map((p) => (
                  <li key={p.produit}>
                    <span className="frc-barres__nom">{p.produit}</span>
                    <span className="frc-barres__piste"><span className="frc-barres__barre" style={{ width: `${Math.round((p.qty / maxProduit) * 100)}%` }} /></span>
                    <span className="frc-barres__val">{p.qty}</span>
                  </li>
                ))}
              </ul>
            ) : null}

            {onglet === 'tiroirs' ? (
              <div className="frc-tiroirs">
                {r.tiroirs.filter((t) => trouve(t.caisse, t.salle)).map((t) => (
                  <article key={t.caisse} className="frc-tiroir">
                    <header className="frc-carte__tete">
                      <span className="frc-carte__nom">{caisseCourte(t.caisse)}</span>
                      <span className="frc-carte__total">{t.ouvertures.length}</span>
                    </header>
                    <p className="frc-tiroir__heures">{t.ouvertures.map((o) => heureParis(o)).join(' · ')}</p>
                  </article>
                ))}
              </div>
            ) : null}

            {onglet === 'caisses' ? (
              <>
                <div className="fi-tablewrap">
                  <table className="fi-table fi-table--chiffres">
                    <colgroup><col /><col className="fi-col-montant" /><col className="fi-col-montant" /><col className="fi-col-montant" /><col className="fi-col-montant" /></colgroup>
                    <thead>
                      <tr>
                        <th scope="col">Caisse</th>
                        <th scope="col" className="fi-money">Carte bancaire</th>
                        <th scope="col" className="fi-money">Espèces</th>
                        <th scope="col" className="fi-money">Carte serveur</th>
                        <th scope="col" className="fi-money">Total encaissé</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.caisses.filter((c) => trouve(c.caisse)).map((c) => (
                        <tr key={c.caisse}>
                          <th scope="row"><span className="fi-cell-titre">{caisseCourte(c.caisse)}</span></th>
                          <td className="fi-money">{formatMoney(c.par_moyen['Carte Bleue'] ?? 0)}</td>
                          <td className="fi-money">{formatMoney(c.par_moyen['Espèce'] ?? 0)}</td>
                          <td className="fi-money">{c.par_moyen['Carte Serveur'] ? formatMoney(c.par_moyen['Carte Serveur']) : '—'}</td>
                          <td className="fi-money fi-money--fort">{formatMoney(c.total_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {r.commentaires.length > 0 ? (
                  <div className="frc-notes">
                    <h3 className="frc-titre">Notes de ZAP</h3>
                    <ul>{r.commentaires.map((c) => <li key={c}>{c}</li>)}</ul>
                  </div>
                ) : null}
              </>
            ) : null}

            <div className="frc-pieces">
              <span className="lc-hint">Pièces :</span>
              {(onglet === 'offerts' ? pieces('CARTES') : onglet === 'doses' ? pieces('DOSEURS') : onglet === 'produits' ? pieces('PRODUITS')
                : onglet === 'tiroirs' ? pieces('TIROIRS') : pieces('CAISSE')).map((p, _i, l) => (
                <VoirSource key={p.id} doc={p} liste={l} className="fiw-piece">{p.filename.replace(/^\d{12}_/, '').replace(/\.pdf$/i, '')}</VoirSource>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </section>
  )
}
